import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MessageModule } from 'primeng/message';
import { NotificationService } from '../../services/notification.service';
import { INotification } from '../../interfaces';
import {
  NotificationPhase,
  NotificationTarget,
  IPimNotificationContext,
  IPimNotificationDisplay,
  EMPTY_PIM_NOTIFICATION_CONTEXT,
} from '../../pim-notification-interface';
import { appPhases } from '../../types';

/** Maps a PIM2 phase to the legacy notifications.json (Wbe) lowercase phase. */
const PIM2_TO_LEGACY_PHASE: Partial<Record<NotificationPhase, appPhases>> = {
  Home: 'home',
  Availability: 'availability',
  Profile: 'profile',
  Confirmation: 'confirmation',
  DatePicker: 'datepicker',
};

@Component({
  selector: 'app-pim-notification',
  standalone: true,
  imports: [CommonModule, MessageModule],
  templateUrl: './pim-notification.component.html',
  styleUrl: './pim-notification.component.scss',
})
export class PimNotificationComponent implements OnInit {
  @Input({ required: true }) phase: NotificationPhase;
  @Input() target: NotificationTarget = 'Wbe';
  @Input() includeLegacy: boolean = true;
  /**
   * Explicit PIM2 evaluation context. Apps without a Wbe searchmask (e.g. table
   * reservation) build their own context and pass it here. When omitted, the Wbe
   * context is built for the Wbe target and an empty context for every other target.
   */
  @Input() context?: IPimNotificationContext;

  /** Legacy notifications.json items (Wbe only). */
  legacyNotifications: INotification[] = [];
  /** PIM2 notifications resolved for display. */
  pimNotifications: IPimNotificationDisplay[] = [];

  constructor(private notificationService: NotificationService) {}

  ngOnInit(): void {
    // Legacy notifications.json only applies to the Wbe (booking-engine) app.
    // includeLegacy=false lets callers opt out (e.g. datepicker handles its own legacy rendering).
    if (this.target === 'Wbe' && this.includeLegacy) {
      const legacyPhase = PIM2_TO_LEGACY_PHASE[this.phase];
      if (legacyPhase) {
        this.legacyNotifications = this.notificationService.getfilteredNotificationsForPhase(legacyPhase);
      }
    }

    const context = this.context
      ?? (this.target === 'Wbe'
        ? this.notificationService.buildWbeContext()
        : { ...EMPTY_PIM_NOTIFICATION_CONTEXT });

    this.pimNotifications = this.notificationService.getPim2NotificationsForPhase(
      this.phase,
      this.target,
      context
    );
  }

  mapLegacyColor(color: string) {
    return this.notificationService.mapNotificationColor(color);
  }

  mapPimColor(color?: string) {
    // PIM2 has no color field yet — default neutral; honor it if present.
    return this.notificationService.mapNotificationColor(color ?? 'theme');
  }
}
